import React from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { colors } from "../theme";

interface Supplier {
  name: string;
  logo: string;
  description: string[];
  website: string;
}

const Suppliers: React.FC = () => {
  const suppliers: Supplier[] = [
    {
      name: "Danfoss",
      logo: "/danfoss.jpg",
      description: [
        "LOW VOLTAGE AC DRIVES UP TO 1500 HP.",
        "STOCK 4X DRIVES UP TO 125 HP.",
        "CUSTOM DRIVE PACKAGES FOR ALL INDUSTRIES.",
        "LOW HARMONIC RATED DRIVE SYSTEMS.",
      ],
      website: "https://www.danfoss.com/en/",
    },
    {
      name: "PULS",
      logo: "/Puls-logo.jpg",
      description: [
        "With more than 40 years of excellence in the industry, PULS is an industry leader in the development and manufacturing of DIN rail power supplies.",
        "DIN RAIL POWER SUPPLIES AND SUPPLEMENTARY UNITS AVAILABLE.",
      ],
      website: "https://www.pulspower.com/",
    },
    {
      name: "TCI (Transcoil)",
      logo: "/transcoil.jpg",
      description: [
        "HARMONIC MITIGATION FOR ALL AC DRIVES.",
        "INPUT & OUTPUT FILTERS IN STOCK.",
        "INDOOR & OUTDOOR RATED ENCLOSURES.",
        "COMPUTER GENERATED HARMONIC ANALYSIS.",
      ],
      website: "https://transcoil.com/",
    },
    {
      name: "Pfannenberg",
      logo: "/pfannenberg.jpg",
      description: [
        "THERMAL MANAGEMENT SOLUTIONS.",
        "AC UNITS TO 24,000 BTU MANUFACTURED IN BUFFALO, NY.",
        "LIQUID AND AIR/AIR HEAT EXCHANGERS.",
        "CHILLERS TO 30 TONS.",
        "LIGHTS AND ALARMS.",
      ],
      website: "https://www.pfannenbergusa.com/",
    },
    {
      name: "Socomec",
      logo: "/Socomec-logo.jpg",
      description: [
        "DISCONNECT SOLUTIONS UP THRU 1600 AMPS.",
        "FUSED AND NON-FUSED UNITS AVAILABLE.",
        "REVENUE GRADE SINGLE & MULTIPOINT METERING/ MONITORING.",
      ],
      website: "https://www.socomec.us/",
    },
    {
      name: "Citel",
      logo: "/Citel-logo.jpg",
      description: [
        "LARGEST SUPPLIER OF SURGE SUPPRESSION.",
        "SPECIALIZATION IN SURGE SUPPRESSION FOR AC, DC, DATA, LED AND SECURITY FOR THE FOLLOWING INDUSTRIES: DOT, RAIL, SOLAR SECURITY, BUILDING AND ANTENNA COMMUNICATION.",
      ],
      website: "https://citel.us/en",
    },
    {
      name: "Lovato",
      logo: "/Lovato-logo.jpg",
      description: [
        "FULL LINE OF STARTERS, DISCONNECTS & MINI BREAKERS.",
        "22 MM 4X RATED PUSH BUTTONS.",
        "POWER FACTOR CONTROLLERS.",
        "AUTO TRANSFER SWITCHES.",
      ],
      website: "https://www.lovatoelectric.com/us_en",
    },
    {
      name: "Macromatic",
      logo: "/Macromatic-logo.jpg",
      description: ["TIMER RELAYS, PUMP CONTROL & INTRINSICALLY SAFE RELAYS."],
      website: "https://www.macromatic.com/",
    },
    {
      name: "Bonitron",
      logo: "/Bonitron-logo.jpg",
      description: [
        "Overvoltage braking solutions, such as Braking Transistors, Resistors, and Line Regeneration, to Undervoltage Solutions, such as UPD Uninterruptible Power for Drives, Single and 3-Phase Power Supplies, as well as Portable Capacitor Formers.",
      ],
      website: "https://www.bonitron.com/",
    },
    {
      name: "AttaBox",
      logo: "/AttaBox-logo.jpg",
      description: [
        "LARGEST SELECTION OF NON-METALLIC ENCLOSURES.",
        'DOUBLE DOOR ENCLOSURES UP TO 92".',
        "CUSTOM DRILLING AND SILK SCREENING AVAILABLE.",
      ],
      website: "https://attabox.com/",
    },
    {
      name: "Boltswitch",
      logo: "/Boltswitch Logo.jpg",
      description: [
        "SINGLE THROW FUSIBLE BOLTED PRESSURE CONTACT SWITCHES.",
        "DOUBLE THROW FUSIBLE BOLTED PRESSURE CONTACT SWITCHES.",
        "FUSIBLE PULLOUT SWITCHES.",
        "ARC FLASH SOLUTIONS.",
      ],
      website: "https://www.boltswitch.com",
    },
  ];

  return (
    <Box sx={{ overflow: "hidden" }}>
      {/* Page Header */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.secondaryDark} 100%)`,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            width: "40%",
            height: "100%",
            background: `linear-gradient(135deg, transparent 0%, rgba(245, 166, 35, 0.1) 100%)`,
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography
              variant="overline"
              sx={{
                color: colors.primary,
                fontWeight: 600,
                letterSpacing: 2,
                mb: 1,
                display: "block",
              }}
            >
              Our Partners
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2.5rem", md: "3.5rem" },
                color: "#FFFFFF",
                fontWeight: 700,
                mb: 2,
              }}
            >
              Suppliers For RPM Control
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "rgba(255, 255, 255, 0.8)",
                fontSize: "1.1rem",
                maxWidth: 500,
              }}
            >
              Quality manufacturers we proudly represent
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Accent Line */}
      <Box
        sx={{
          height: 4,
          background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
        }}
      />

      {/* Quote CTA Section */}
      <Box
        sx={{
          py: { xs: 5, md: 6 },
          background: colors.background,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h5"
              sx={{
                color: colors.secondary,
                mb: 3,
                fontWeight: 600,
              }}
            >
              Looking for a specific product?
            </Typography>
            <Button
              component={Link}
              to="/contact"
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
            >
              Need a Quote Right Now?
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Manufacturers Header */}
      <Box
        sx={{
          py: { xs: 4, md: 5 },
          background: "#FFFFFF",
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="overline"
              sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2 }}
            >
              Trusted Brands
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "1.75rem", md: "2.25rem" },
                color: colors.secondary,
                fontWeight: 700,
                mt: 1,
              }}
            >
              Manufacturers We Proudly Represent
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Suppliers Grid */}
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          background: "#FFFFFF",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {suppliers.map((supplier, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  component="a"
                  href={supplier.website}
                  target={
                    supplier.website.startsWith("http") ? "_blank" : "_self"
                  }
                  rel={
                    supplier.website.startsWith("http")
                      ? "noopener noreferrer"
                      : undefined
                  }
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    textDecoration: "none",
                    border: "1px solid",
                    borderColor: colors.border,
                    borderRadius: 3,
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: colors.primary,
                      transform: "scaleX(0)",
                      transition: "transform 0.3s ease",
                    },
                    "&:hover": {
                      boxShadow: `0 16px 40px rgba(245, 166, 35, 0.15)`,
                      transform: "translateY(-6px)",
                      borderColor: colors.primary,
                      "&::before": {
                        transform: "scaleX(1)",
                      },
                      "& .external-icon": {
                        opacity: 1,
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      p: 3,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: 120,
                      borderBottom: "1px solid",
                      borderColor: colors.border,
                      background: colors.background,
                      position: "relative",
                    }}
                  >
                    <Box
                      component="img"
                      src={supplier.logo}
                      alt={supplier.name}
                      sx={{
                        maxWidth: "100%",
                        maxHeight: 70,
                        objectFit: "contain",
                      }}
                    />
                    {supplier.website.startsWith("http") && (
                      <OpenInNewIcon
                        className="external-icon"
                        sx={{
                          position: "absolute",
                          top: 12,
                          right: 12,
                          fontSize: 16,
                          color: colors.primary,
                          opacity: 0,
                          transition: "opacity 0.3s ease",
                        }}
                      />
                    )}
                  </Box>
                  <CardContent sx={{ flex: 1, p: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: colors.secondary,
                        fontWeight: 700,
                        mb: 2,
                        fontSize: "1rem",
                      }}
                    >
                      {supplier.name}
                    </Typography>
                    {supplier.description.map((line, i) => (
                      <Typography
                        key={i}
                        variant="body2"
                        sx={{
                          color: colors.textSecondary,
                          fontSize: "0.85rem",
                          lineHeight: 1.6,
                          mb: i < supplier.description.length - 1 ? 1 : 0,
                        }}
                      >
                        {line}
                      </Typography>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "1.75rem", md: "2.25rem" },
                color: "#FFFFFF",
                mb: 3,
              }}
            >
              Can't find what you're looking for?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "rgba(255, 255, 255, 0.9)",
                fontSize: "1.1rem",
                mb: 4,
                maxWidth: 500,
                mx: "auto",
              }}
            >
              Contact us today and we'll help you find the right solution for
              your needs.
            </Typography>
            <Button
              component={Link}
              to="/contact"
              variant="contained"
              size="large"
              sx={{
                backgroundColor: "#FFFFFF",
                color: colors.primary,
                px: 5,
                py: 1.5,
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  boxShadow: "0 8px 30px rgba(255, 255, 255, 0.3)",
                },
              }}
            >
              Contact Us
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Suppliers;
